import React from 'react';
import PropTypes from 'prop-types';
import { candidateCellColumn } from './CandidateCell';
import { mappedAtColumn } from './sharedColumns';
import RowActionsCell from './RowActionsCell';
import SelectableTabTable from './SelectableTabTable';
import { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import { registerTab } from './tabRegistry';

// Default flow — "New" tab. Candidates land here before a test has been
// assigned. Selecting rows surfaces whatever manual (state-transition)
// actions the "New" status carries in the live flow-statuses response
// (GET /assignments/:id/flow-statuses → statuses[].manual_actions) — in
// practice that's { key: 'assign_test', label: 'Assign Test' } today, the
// same action the old RequirementPipeline.jsx's "Send test link" button drove for
// TA users on this tab, but this tab doesn't hardcode that key: it's
// resolved generically by ManualActionsBar (via SelectableTabTable) off
// whatever manual_actions is passed in. Registry key matches the
// flow-statuses API's exact status string ("New").
const NewCandidatesTab = ({
  candidates,
  isLoading,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  opportunityId,
  assignmentId,
  requirementLocationId,
  stateMachineKey,
  manualActions,
  onActionSuccess,
  onViewProfile,
}) => {
  const columns = [
    candidateCellColumn,
    mappedAtColumn,
    {
      key: 'row_actions',
      header: '',
      className: 'text-sm',
      render: (_v, row) => (
        <RowActionsCell
          candidateId={row.candidate_id}
          mappingId={row.id}
          status="New"
          opportunityId={opportunityId}
          assignmentId={assignmentId}
          requirementLocationId={requirementLocationId}
          stateMachineKey={stateMachineKey}
          manualActions={manualActions}
          onActionSuccess={onActionSuccess}
          onViewProfile={onViewProfile}
        />
      ),
    },
  ];

  return (
    <SelectableTabTable
      columns={columns}
      candidates={candidates}
      isLoading={isLoading}
      page={page}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      opportunityId={opportunityId}
      stateMachineKey={stateMachineKey}
      manualActions={manualActions}
      onActionSuccess={onActionSuccess}
      emptyState={<TabEmptyState description="No new candidates yet." />}
    />
  );
};

NewCandidatesTab.propTypes = {
  candidates: PropTypes.arrayOf(candidateShape),
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  onActionSuccess: PropTypes.func,
  onViewProfile: PropTypes.func,
};

NewCandidatesTab.defaultProps = {
  candidates: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  opportunityId: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
  stateMachineKey: 'default',
  manualActions: [],
  onActionSuccess: () => {},
  onViewProfile: () => {},
};

registerTab({
  stateMachineKey: 'default',
  state: 'New',
  component: NewCandidatesTab,
});

export default NewCandidatesTab;
